import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Train, Plus, Edit, Trash2, IndianRupee, Loader2, Activity,
  Users, RefreshCw, Wallet, ArrowDownUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const RS = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const empty = { name: '', railtel_package_id: '', description: '', master_price: '', validity_days: 30, is_active: true };

// 1×1 px valid JPEG, base64 — Railtel requires Id_Photo for kycdata
const TINY_JPEG = '/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAj/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFAEBAAAAAAAAAAAAAAAAAAAAAP/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AL+AAf/Z';

const emptyKyc = {
  state: 'Karnataka', name: '', uname: '', email: '', mobileno: '',
  apno: '', dateofbirth: '1990-01-01', doorno: '101', block: 'Test Block A',
  streetlo: 'Test Street', cityname: 'Bangalore', village: 'Test Village', district: 'Bangalore',
  pincode: '560001', idproof_no: '', id_type: 'aadhar', Id_Photo: TINY_JPEG,
  partnerid: '6219361247',
};

export default function Railtel() {
  const { user } = useAuth();
  const userRole = user?.role || (user?.roles || [])[0] || '';
  const isSupport = userRole === 'support';
  const [tab, setTab] = useState('packages');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerPricingPkg, setPartnerPricingPkg] = useState(null);
  const [partnerPricingForm, setPartnerPricingForm] = useState({ customer_id: '', price: '' });
  const [diagBusy, setDiagBusy] = useState(false);

  const [showTestPanel, setShowTestPanel] = useState(false);
  const [testTab, setTestTab] = useState('recharge');
  const [testRecharge, setTestRecharge] = useState({ username: 'ADIGITAL15', railtel_package_id: 801130, stcode: 'KA' });
  const [testCreate, setTestCreate] = useState({
    railtel_package_id: '801130', stcode: 'KA',
    kyc: { ...emptyKyc },
  });
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Subscribers (imported + locally created)
  const [subs, setSubs] = useState([]);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsQ, setSubsQ] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({ uname: '', stcode: 'KA', customer_id: '', name: '', mobileno: '', email: '', package_id: '' });
  const [importBusy, setImportBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showAdminRecharge, setShowAdminRecharge] = useState(null);
  const [adminRechargePkg, setAdminRechargePkg] = useState('');
  const [adminRechargeBusy, setAdminRechargeBusy] = useState(false);

  // Combos
  const [combos, setCombos] = useState([]);
  const [iptvPackages, setIptvPackages] = useState([]);
  const [showComboForm, setShowComboForm] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [comboForm, setComboForm] = useState({
    name: '', railtel_package_id: '', iptv_package_id: '',
    master_price: '', validity_days: 30, description: '', is_active: true,
  });
  const [comboPricingPkg, setComboPricingPkg] = useState(null);
  const [comboPricingForm, setComboPricingForm] = useState({ customer_id: '', price: '' });

  // Manual Top-up / Debit dialog
  const [showTopup, setShowTopup] = useState(false);
  const [topupMode, setTopupMode] = useState('credit');  // 'credit' | 'debit'
  const [topupForm, setTopupForm] = useState({
    customer_id: '', amount: '', payment_mode: 'cash',
    transaction_ref: '', notes: '',
  });
  const [debitForm, setDebitForm] = useState({
    customer_id: '', amount: '', reason: '', notes: '', allow_negative: false,
  });
  const [topupBusy, setTopupBusy] = useState(false);

  // Transactions tab
  const [txns, setTxns] = useState([]);
  const [txnsTotal, setTxnsTotal] = useState(0);
  const [txnsSummary, setTxnsSummary] = useState({ credit_total: 0, debit_total: 0, credit_count: 0, debit_count: 0 });
  const [txnsFilter, setTxnsFilter] = useState({ q: '', customer_id: '', txn_type: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgRes, partnerRes, subsRes, custRes, comboRes, iptvPkgRes] = await Promise.all([
        axios.get(`${API}/api/railtel/packages`, { headers }),
        axios.get(`${API}/api/customers?customer_type=partner`, { headers }),
        axios.get(`${API}/api/railtel/admin/subscribers?limit=200${subsQ ? `&q=${encodeURIComponent(subsQ)}` : ''}`, { headers }),
        axios.get(`${API}/api/customers`, { headers }),
        axios.get(`${API}/api/railtel/combos`, { headers }),
        axios.get(`${API}/api/iptv/packages`, { headers }),
      ]);
      setPackages(pkgRes.data || []);
      setPartners(partnerRes.data || []);
      setSubs(subsRes.data?.subscribers || []);
      setSubsTotal(subsRes.data?.total || 0);
      setCombos(comboRes.data || []);
      setIptvPackages((iptvPkgRes.data?.packages || iptvPkgRes.data || []).filter(p => p.is_active !== false));
      const all = custRes.data || [];
      // Keep only partners + LCOs (have parent_partner_id) for mapping
      setCustomers(all.filter(c => c.customer_type === 'partner' || c.parent_partner_id));
    } catch { toast.error('Failed to load Railtel data'); }
    setLoading(false);
  }, [subsQ]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.railtel_package_id || !form.master_price) {
      toast.error('Name, Railtel Package ID and Master Price are required'); return;
    }
    const body = {
      ...form,
      master_price: parseFloat(form.master_price),
      validity_days: parseInt(form.validity_days, 10),
    };
    try {
      if (editing) {
        await axios.put(`${API}/api/railtel/packages/${editing.package_id}`, body, { headers });
        toast.success('Package updated');
      } else {
        await axios.post(`${API}/api/railtel/packages`, body, { headers });
        toast.success('Package created');
      }
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try { await axios.delete(`${API}/api/railtel/packages/${id}`, { headers }); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const savePartnerPricing = async () => {
    if (!partnerPricingForm.customer_id || !partnerPricingForm.price) {
      toast.error('Pick partner and price'); return;
    }
    try {
      await axios.put(
        `${API}/api/railtel/packages/${partnerPricingPkg.package_id}/partner-pricing`,
        { customer_id: partnerPricingForm.customer_id, price: parseFloat(partnerPricingForm.price) },
        { headers }
      );
      toast.success('Partner pricing saved');
      setPartnerPricingPkg(null);
      setPartnerPricingForm({ customer_id: '', price: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const saveCombo = async () => {
    if (!comboForm.name || !comboForm.railtel_package_id || !comboForm.iptv_package_id || !comboForm.master_price) {
      toast.error('Name, Railtel pkg, IPTV pkg and price are required'); return;
    }
    const body = {
      ...comboForm,
      master_price: parseFloat(comboForm.master_price),
      validity_days: parseInt(comboForm.validity_days, 10),
    };
    try {
      if (editingCombo) {
        await axios.put(`${API}/api/railtel/combos/${editingCombo.combo_id}`, body, { headers });
        toast.success('Combo updated');
      } else {
        await axios.post(`${API}/api/railtel/combos`, body, { headers });
        toast.success('Combo created');
      }
      setShowComboForm(false); setEditingCombo(null);
      setComboForm({ name: '', railtel_package_id: '', iptv_package_id: '', master_price: '', validity_days: 30, description: '', is_active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const removeCombo = async (id) => {
    if (!window.confirm('Delete this combo?')) return;
    try { await axios.delete(`${API}/api/railtel/combos/${id}`, { headers }); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const saveComboPartnerPricing = async () => {
    if (!comboPricingForm.customer_id || !comboPricingForm.price) { toast.error('Pick partner and price'); return; }
    try {
      await axios.put(
        `${API}/api/railtel/combos/${comboPricingPkg.combo_id}/partner-pricing`,
        { customer_id: comboPricingForm.customer_id, price: parseFloat(comboPricingForm.price) },
        { headers }
      );
      toast.success('Combo partner pricing saved');
      setComboPricingPkg(null); setComboPricingForm({ customer_id: '', price: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const runDiagnostic = async () => {
    setDiagBusy(true);
    try {
      const r = await axios.post(`${API}/api/railtel/admin/test-validate`,
        { stcode: 'KA', mobileno: '9999999999', username: 'DIAG_PROBE_X', email: 'diag@example.com' },
        { headers });
      if (r.data.ok) toast.success('Provider reachable: ' + r.data.message);
      else toast.warning('Provider responded: ' + r.data.message);
    } catch (e) { toast.error('Provider call failed'); }
    setDiagBusy(false);
  };

  const runTestRecharge = async () => {
    setTestBusy(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/api/railtel/admin/test-recharge`, {
        username: testRecharge.username,
        railtel_package_id: parseInt(testRecharge.railtel_package_id, 10),
        stcode: testRecharge.stcode,
      }, { headers });
      setTestResult(r.data);
      if (r.data.recharge?.ok) toast.success('Recharge OK: ' + r.data.recharge.message);
      else toast.warning('Recharge response: ' + (r.data.recharge?.message || 'unknown'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTestBusy(false);
  };

  const runTestCreate = async () => {
    const k = testCreate.kyc;
    if (!k.name || !k.uname || !k.email || !k.mobileno || !k.idproof_no) {
      toast.error('Fill name, uname, email, mobile, ID number'); return;
    }
    setTestBusy(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/api/railtel/admin/test-create`, {
        kyc: testCreate.kyc,
        railtel_package_id: testCreate.railtel_package_id,
        stcode: testCreate.stcode,
      }, { headers });
      setTestResult(r.data);
      if (r.data.create?.ok) toast.success('Created: ' + (r.data.create.extra?.username || 'OK'));
      else if (!r.data.validate?.ok) toast.warning('Validate blocked: ' + r.data.validate?.message);
      else toast.warning('Create response: ' + r.data.create?.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTestBusy(false);
  };

  const runImport = async () => {
    if (!importForm.uname || !importForm.customer_id) { toast.error('Username and Customer mapping required'); return; }
    setImportBusy(true);
    try {
      const r = await axios.post(`${API}/api/railtel/admin/import-subscriber`, {
        ...importForm,
        package_id: importForm.package_id || null,
      }, { headers });
      if (r.data.success) {
        toast.success(`Imported ${r.data.subscriber.uname} (status: ${r.data.subscriber.status})`);
        setShowImport(false);
        setImportForm({ uname: '', stcode: 'KA', customer_id: '', name: '', mobileno: '', email: '', package_id: '' });
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setImportBusy(false);
  };

  const runAdminRecharge = async () => {
    if (!showAdminRecharge || !adminRechargePkg) { toast.error('Pick a package'); return; }
    setAdminRechargeBusy(true);
    try {
      const r = await axios.post(`${API}/api/railtel/admin/recharge`, {
        uname: showAdminRecharge.uname,
        stcode: showAdminRecharge.stcode,
        package_id: adminRechargePkg,
      }, { headers });
      if (r.data.success) {
        toast.success(`Recharged — valid till ${r.data.servicedate || '—'} (Rs.${r.data.wallet_charged} debited)`);
        setShowAdminRecharge(null);
        setAdminRechargePkg('');
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Recharge failed'); }
    setAdminRechargeBusy(false);
  };

  const runTopup = async () => {
    if (topupMode === 'credit') {
      if (!topupForm.customer_id || !topupForm.amount || parseFloat(topupForm.amount) <= 0) {
        toast.error('Pick a customer and enter a positive amount'); return;
      }
      setTopupBusy(true);
      try {
        const r = await axios.post(`${API}/api/railtel/admin/wallet/credit`, {
          customer_id: topupForm.customer_id,
          amount: parseFloat(topupForm.amount),
          payment_mode: topupForm.payment_mode,
          transaction_ref: topupForm.transaction_ref,
          notes: topupForm.notes,
        }, { headers });
        if (r.data.success) {
          toast.success(`Credited ${RS(parseFloat(topupForm.amount))} to ${r.data.company_name} — new balance ${RS(r.data.new_balance)}`);
          setShowTopup(false);
          setTopupForm({ customer_id: '', amount: '', payment_mode: 'cash', transaction_ref: '', notes: '' });
          if (tab === 'transactions') loadTxns();
        }
      } catch (e) { toast.error(e.response?.data?.detail || 'Top-up failed'); }
      setTopupBusy(false);
    } else {
      // Debit mode
      if (!debitForm.customer_id || !debitForm.amount || parseFloat(debitForm.amount) <= 0) {
        toast.error('Pick a customer and enter a positive amount'); return;
      }
      if (!debitForm.reason.trim()) {
        toast.error('Reason is required for a debit'); return;
      }
      setTopupBusy(true);
      try {
        const r = await axios.post(`${API}/api/railtel/admin/wallet/debit`, {
          customer_id: debitForm.customer_id,
          amount: parseFloat(debitForm.amount),
          reason: debitForm.reason,
          notes: debitForm.notes,
          allow_negative: debitForm.allow_negative,
        }, { headers });
        if (r.data.success) {
          toast.success(`Debited ${RS(parseFloat(debitForm.amount))} from ${r.data.company_name} — new balance ${RS(r.data.new_balance)}`);
          setShowTopup(false);
          setDebitForm({ customer_id: '', amount: '', reason: '', notes: '', allow_negative: false });
          if (tab === 'transactions') loadTxns();
        }
      } catch (e) { toast.error(e.response?.data?.detail || 'Debit failed'); }
      setTopupBusy(false);
    }
  };

  const loadTxns = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (txnsFilter.q) params.append('q', txnsFilter.q);
      if (txnsFilter.customer_id) params.append('customer_id', txnsFilter.customer_id);
      if (txnsFilter.txn_type) params.append('txn_type', txnsFilter.txn_type);
      const r = await axios.get(`${API}/api/railtel/admin/transactions?${params.toString()}`, { headers });
      setTxns(r.data.transactions || []);
      setTxnsTotal(r.data.total || 0);
      setTxnsSummary(r.data.summary || { credit_total: 0, debit_total: 0, credit_count: 0, debit_count: 0 });
    } catch { toast.error('Failed to load transactions'); }
  }, [txnsFilter]); // eslint-disable-line

  useEffect(() => { if (tab === 'transactions') loadTxns(); }, [tab, loadTxns]);

  return (
    <div className="p-6 space-y-6" data-testid="railtel-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Train className="h-6 w-6" /> ANT+RAILTEL
          </h1>
          <p className="text-sm text-slate-500 mt-1">Master packages, combos, per-partner pricing, and provider diagnostics</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runDiagnostic} disabled={diagBusy} data-testid="railtel-diag-btn">
            {diagBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Activity className="h-4 w-4 mr-1" />}
            Provider Health
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowTestPanel(true); setTestResult(null); }} data-testid="railtel-test-panel-btn">
            <Activity className="h-4 w-4 mr-1" /> Test Live
          </Button>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          {!isSupport && (
            <Button variant="outline" onClick={() => { setTopupForm({ customer_id: '', amount: '', payment_mode: 'cash', transaction_ref: '', notes: '' }); setShowTopup(true); }} data-testid="railtel-admin-topup-btn">
              <Wallet className="h-4 w-4 mr-1" /> Top Up Wallet
            </Button>
          )}
          {!isSupport && (
            <Button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }} data-testid="railtel-new-pkg-btn">
              <Plus className="h-4 w-4 mr-1" /> New Package
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="packages" data-testid="railtel-tab-packages">Packages ({packages.length})</TabsTrigger>
          <TabsTrigger value="combos" data-testid="railtel-tab-combos">Combos ({combos.length})</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="railtel-tab-subs">Subscribers ({subsTotal})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="railtel-tab-txns">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="packages">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Master Pricing</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
              ) : packages.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Train className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p>No Railtel packages yet</p>
                  <p className="text-xs mt-1">Add Railtel packageid (e.g. 100050) and your master price</p>
                </div>
              ) : (
                <div className="divide-y">
                  {packages.map(p => (
                    <div key={p.package_id} className="p-4 flex flex-wrap items-center gap-3 hover:bg-slate-50" data-testid={`railtel-pkg-${p.package_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{p.name}</p>
                          <Badge variant="outline">Railtel #{p.railtel_package_id}</Badge>
                          <Badge className={p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                            {p.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{p.description || '—'}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Validity: {p.validity_days} days
                          {p.custom_pricing?.length ? ` • ${p.custom_pricing.length} partner override${p.custom_pricing.length > 1 ? 's' : ''}` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Master Price</p>
                        <p className="text-lg font-bold text-slate-900">{RS(p.master_price)}</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setPartnerPricingPkg(p); setPartnerPricingForm({ customer_id: '', price: '' }); }}
                                data-testid={`railtel-partner-price-${p.package_id}`}>
                          <Users className="h-3.5 w-3.5 mr-1" /> Partner Price
                        </Button>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setForm({ ...p, master_price: String(p.master_price) }); setShowForm(true); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p.package_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="combos" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingCombo(null); setComboForm({ name: '', railtel_package_id: '', iptv_package_id: '', master_price: '', validity_days: 30, description: '', is_active: true }); setShowComboForm(true); }} data-testid="railtel-new-combo-btn">
              <Plus className="h-4 w-4 mr-1" /> New Combo
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {combos.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <p>No combos yet</p>
                  <p className="text-xs mt-1">Combos bundle a Railtel package + IPTV package at one combined price for partners/LCOs</p>
                </div>
              ) : (
                <div className="divide-y">
                  {combos.map(c => {
                    const rPkg = packages.find(p => p.package_id === c.railtel_package_id);
                    const iPkg = iptvPackages.find(p => p.package_id === c.iptv_package_id);
                    return (
                      <div key={c.combo_id} className="p-4 hover:bg-slate-50" data-testid={`railtel-combo-${c.combo_id}`}>
                        <div className="flex items-start gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-slate-900">{c.name}</p>
                              <Badge className="bg-purple-100 text-purple-700">ANT+RAILTEL</Badge>
                              {!c.is_active && <Badge variant="outline">Inactive</Badge>}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              <span className="text-orange-600">Railtel:</span> {rPkg?.name || '—'} (#{rPkg?.railtel_package_id || '—'})
                              {' • '}
                              <span className="text-indigo-600">IPTV:</span> {iPkg?.name || '—'}
                              {' • '}
                              Validity {c.validity_days}d
                            </p>
                            {c.description && <p className="text-xs text-slate-400 mt-0.5">{c.description}</p>}
                            {c.custom_pricing?.length > 0 && (
                              <p className="text-xs text-slate-400 mt-0.5">Per-partner overrides: {c.custom_pricing.length}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Master Price</p>
                            <p className="text-lg font-bold text-purple-600">{RS(c.master_price)}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Button size="sm" variant="outline" onClick={() => { setComboPricingPkg(c); setComboPricingForm({ customer_id: '', price: c.master_price }); }} data-testid={`combo-pricing-${c.combo_id}`}>
                            <Users className="h-3.5 w-3.5 mr-1" /> Partner Pricing
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingCombo(c); setComboForm({ name: c.name, railtel_package_id: c.railtel_package_id, iptv_package_id: c.iptv_package_id, master_price: c.master_price, validity_days: c.validity_days, description: c.description || '', is_active: c.is_active }); setShowComboForm(true); }}>
                            <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => removeCombo(c.combo_id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscribers" className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <Input placeholder="Search by uname / name / mobile..." value={subsQ}
                     onChange={e => setSubsQ(e.target.value)} data-testid="railtel-sub-search" />
            </div>
            <Button onClick={() => setShowImport(true)} data-testid="railtel-import-btn">
              <Plus className="h-4 w-4 mr-1" /> Import Existing Subscriber
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              {subs.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <p>No subscribers yet</p>
                  <p className="text-xs mt-1">Import existing users from your old portal → they'll be mapped to a partner or LCO here</p>
                </div>
              ) : (
                <div className="divide-y">
                  {subs.map(s => (
                    <div key={s.subscriber_id} className="p-4 flex items-center gap-3 hover:bg-slate-50" data-testid={`railtel-adm-sub-${s.uname}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{s.uname}</p>
                          <Badge variant="outline">{s.stcode}</Badge>
                          <Badge className={s.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                                            : s.status === 'inactive' ? 'bg-amber-100 text-amber-700'
                                            : 'bg-slate-200 text-slate-600'}>
                            {s.status}
                          </Badge>
                          {s.imported && <Badge className="bg-blue-100 text-blue-700">Imported</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{s.name || '—'} • {s.mobileno || '—'}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Mapped to: {customers.find(c => c.customer_id === s.customer_id)?.company_name || s.customer_id}
                          {s.last_servicedate && ` • valid till ${s.last_servicedate}`}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => { setShowAdminRecharge(s); setAdminRechargePkg(s.package_id || ''); }}
                              data-testid={`railtel-adm-rch-${s.uname}`}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Admin Recharge
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Credits</p>
              <p className="text-lg font-bold text-emerald-600" data-testid="railtel-txn-credit-total">{RS(txnsSummary.credit_total)}</p>
              <p className="text-xs text-slate-400">{txnsSummary.credit_count} txns</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Debits</p>
              <p className="text-lg font-bold text-red-600" data-testid="railtel-txn-debit-total">{RS(txnsSummary.debit_total)}</p>
              <p className="text-xs text-slate-400">{txnsSummary.debit_count} txns</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-slate-500">Net</p>
              <p className={`text-lg font-bold ${txnsSummary.credit_total - txnsSummary.debit_total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {RS(txnsSummary.credit_total - txnsSummary.debit_total)}
              </p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Records</p>
              <p className="text-lg font-bold text-slate-700">{txnsTotal}</p>
            </CardContent></Card>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search description..."
              value={txnsFilter.q}
              onChange={e => setTxnsFilter({ ...txnsFilter, q: e.target.value })}
              className="max-w-xs"
              data-testid="railtel-txn-search"
            />
            <Select value={txnsFilter.customer_id || 'all'} onValueChange={v => setTxnsFilter({ ...txnsFilter, customer_id: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[260px]" data-testid="railtel-txn-cust-filter"><SelectValue placeholder="Filter by customer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.customer_id} value={c.customer_id}>
                    {c.company_name}{c.parent_partner_id ? ' (LCO)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={txnsFilter.txn_type || 'all'} onValueChange={v => setTxnsFilter({ ...txnsFilter, txn_type: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[140px]" data-testid="railtel-txn-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
                <SelectItem value="debit">Debit</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadTxns} data-testid="railtel-txn-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {txns.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <ArrowDownUp className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p>No transactions found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {txns.map(t => (
                    <div key={t.transaction_id} className="p-3 grid grid-cols-12 gap-2 items-center text-sm hover:bg-slate-50" data-testid={`railtel-txn-row-${t.transaction_id}`}>
                      <div className="col-span-3">
                        <p className="font-medium text-slate-700 truncate">{t.customer_name || t.customer_id?.slice(0, 8) || '—'}</p>
                        <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="col-span-5">
                        <p className="text-slate-700 truncate">{t.description}</p>
                        {t.reference_id && <p className="text-xs text-slate-400 truncate">Ref: {t.reference_id}</p>}
                      </div>
                      <div className="col-span-2 text-xs">
                        {t.payment_mode && <Badge variant="outline" className="mr-1">{t.payment_mode}</Badge>}
                        {t.linked_partner_id && <Badge className="bg-indigo-100 text-indigo-700">LCO</Badge>}
                      </div>
                      <div className="col-span-2 text-right">
                        <p className={`font-bold ${t.type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {t.type === 'credit' ? '+' : '−'}{RS(t.amount)}
                        </p>
                        {t.status && <p className="text-[10px] uppercase tracking-wide text-slate-400">{t.status}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Package Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="railtel-pkg-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} Railtel Package</DialogTitle>
            <DialogDescription>Master price is what partners pay (their default cost).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Display Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="Railwire 30 Days" data-testid="railtel-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Railtel Package ID *</Label>
                <Input value={form.railtel_package_id}
                       onChange={e => setForm({ ...form, railtel_package_id: e.target.value })}
                       placeholder="e.g. 100050" data-testid="railtel-form-pkgid" />
              </div>
              <div>
                <Label>Validity (days) *</Label>
                <Input type="number" value={form.validity_days}
                       onChange={e => setForm({ ...form, validity_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Master Price (Rs.) *</Label>
              <Input type="number" step="0.01" value={form.master_price}
                     onChange={e => setForm({ ...form, master_price: e.target.value })}
                     data-testid="railtel-form-price" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="cursor-pointer">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} data-testid="railtel-form-save">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Pricing Dialog */}
      <Dialog open={!!partnerPricingPkg} onOpenChange={v => { if (!v) setPartnerPricingPkg(null); }}>
        <DialogContent data-testid="railtel-partner-pricing-dialog">
          <DialogHeader>
            <DialogTitle>Partner Pricing — {partnerPricingPkg?.name}</DialogTitle>
            <DialogDescription>
              Master: <b>{RS(partnerPricingPkg?.master_price)}</b>. Partners with no override pay master price.
            </DialogDescription>
          </DialogHeader>
          {partnerPricingPkg?.custom_pricing?.length > 0 && (
            <div className="border rounded-md divide-y mb-3">
              {partnerPricingPkg.custom_pricing.map(c => {
                const p = partners.find(x => x.customer_id === c.partner_id);
                return (
                  <div key={c.partner_id} className="p-2 flex justify-between items-center text-sm">
                    <span>{p?.company_name || c.partner_id}</span>
                    <span className="font-bold">{RS(c.price)}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label>Partner *</Label>
              <select className="w-full border rounded-md p-2 text-sm"
                      value={partnerPricingForm.customer_id}
                      onChange={e => setPartnerPricingForm({ ...partnerPricingForm, customer_id: e.target.value })}>
                <option value="">Select…</option>
                {partners.map(p => <option key={p.customer_id} value={p.customer_id}>{p.company_name}</option>)}
              </select>
            </div>
            <div>
              <Label>Partner Price (Rs.) *</Label>
              <Input type="number" step="0.01" value={partnerPricingForm.price}
                     onChange={e => setPartnerPricingForm({ ...partnerPricingForm, price: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartnerPricingPkg(null)}>Cancel</Button>
            <Button onClick={savePartnerPricing}><IndianRupee className="h-4 w-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Import Existing Subscriber Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-lg" data-testid="railtel-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Existing Railtel Subscriber</DialogTitle>
            <DialogDescription>Paste a username from your old portal. We'll verify with Railtel and map it to a local partner/LCO.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Railtel Username *</Label>
                <Input value={importForm.uname} onChange={e => setImportForm({ ...importForm, uname: e.target.value })}
                       placeholder="e.g. ADIGITAL15" data-testid="railtel-import-uname" />
              </div>
              <div>
                <Label>State Code *</Label>
                <Input value={importForm.stcode} onChange={e => setImportForm({ ...importForm, stcode: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Map to Customer (Partner or LCO) *</Label>
              <select className="w-full border rounded-md p-2 text-sm"
                      value={importForm.customer_id}
                      onChange={e => setImportForm({ ...importForm, customer_id: e.target.value })}
                      data-testid="railtel-import-customer">
                <option value="">Select…</option>
                <optgroup label="Partners">
                  {customers.filter(c => c.customer_type === 'partner').map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>
                  ))}
                </optgroup>
                <optgroup label="LCOs">
                  {customers.filter(c => c.parent_partner_id).map(c => (
                    <option key={c.customer_id} value={c.customer_id}>
                      {c.company_name} (LCO of {partners.find(p => p.customer_id === c.parent_partner_id)?.company_name || '—'})
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Display Name</Label><Input value={importForm.name} onChange={e => setImportForm({ ...importForm, name: e.target.value })} /></div>
              <div><Label>Mobile</Label><Input value={importForm.mobileno} onChange={e => setImportForm({ ...importForm, mobileno: e.target.value })} /></div>
              <div className="col-span-2"><Label>Email</Label><Input value={importForm.email} onChange={e => setImportForm({ ...importForm, email: e.target.value })} /></div>
            </div>
            <div>
              <Label>Default Package (optional, for later recharges)</Label>
              <select className="w-full border rounded-md p-2 text-sm"
                      value={importForm.package_id}
                      onChange={e => setImportForm({ ...importForm, package_id: e.target.value })}>
                <option value="">— none —</option>
                {packages.map(p => <option key={p.package_id} value={p.package_id}>{p.name} (#{p.railtel_package_id})</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button onClick={runImport} disabled={importBusy} data-testid="railtel-import-save">
              {importBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Verify + Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Recharge Dialog */}
      <Dialog open={!!showAdminRecharge} onOpenChange={v => { if (!v) setShowAdminRecharge(null); }}>
        <DialogContent data-testid="railtel-admin-rch-dialog">
          <DialogHeader>
            <DialogTitle>Admin Recharge — {showAdminRecharge?.uname}</DialogTitle>
            <DialogDescription>
              Charges {customers.find(c => c.customer_id === showAdminRecharge?.customer_id)?.company_name || showAdminRecharge?.customer_id}'s Railtel wallet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Package *</Label>
            <select className="w-full border rounded-md p-2 text-sm"
                    value={adminRechargePkg}
                    onChange={e => setAdminRechargePkg(e.target.value)}>
              <option value="">Pick a package…</option>
              {packages.filter(p => p.is_active).map(p => (
                <option key={p.package_id} value={p.package_id}>{p.name} (#{p.railtel_package_id}) — {RS(p.master_price)}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminRecharge(null)}>Cancel</Button>
            <Button onClick={runAdminRecharge} disabled={adminRechargeBusy} data-testid="railtel-admin-rch-save">
              {adminRechargeBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Recharge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live Test Panel — bypasses wallets / DB, hits Railtel direct */}
      <Dialog open={showTestPanel} onOpenChange={setShowTestPanel}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="railtel-test-panel">
          <DialogHeader>
            <DialogTitle>Test Live Railtel API</DialogTitle>
            <DialogDescription>Direct provider calls — no wallet debit, no DB writes. Use to validate before going live.</DialogDescription>
          </DialogHeader>

          <Tabs value={testTab} onValueChange={setTestTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="recharge" data-testid="test-tab-recharge">Recharge Existing</TabsTrigger>
              <TabsTrigger value="create" data-testid="test-tab-create">Create New User</TabsTrigger>
            </TabsList>

            <TabsContent value="recharge" className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Username *</Label>
                  <Input value={testRecharge.username}
                         onChange={e => setTestRecharge({ ...testRecharge, username: e.target.value })}
                         data-testid="test-rch-username" />
                </div>
                <div>
                  <Label>Railtel Package ID *</Label>
                  <Input type="number" value={testRecharge.railtel_package_id}
                         onChange={e => setTestRecharge({ ...testRecharge, railtel_package_id: e.target.value })}
                         data-testid="test-rch-pkg" />
                </div>
                <div>
                  <Label>State Code *</Label>
                  <Input value={testRecharge.stcode}
                         onChange={e => setTestRecharge({ ...testRecharge, stcode: e.target.value })}
                         data-testid="test-rch-stcode" />
                </div>
              </div>
              <Button onClick={runTestRecharge} disabled={testBusy} data-testid="test-rch-run">
                {testBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Activity className="h-4 w-4 mr-1" />}
                Run Recharge + Status Check
              </Button>
            </TabsContent>

            <TabsContent value="create" className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Railtel Package ID *</Label><Input value={testCreate.railtel_package_id}
                         onChange={e => setTestCreate({ ...testCreate, railtel_package_id: e.target.value })} /></div>
                <div><Label>State Code *</Label><Input value={testCreate.stcode}
                         onChange={e => setTestCreate({ ...testCreate, stcode: e.target.value })} /></div>
                <div><Label>State (full) *</Label><Input value={testCreate.kyc.state}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, state: e.target.value } })} /></div>
                <div><Label>Full Name *</Label><Input value={testCreate.kyc.name}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, name: e.target.value } })} /></div>
                <div><Label>Username (uname) *</Label><Input value={testCreate.kyc.uname}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, uname: e.target.value } })} /></div>
                <div><Label>Email *</Label><Input value={testCreate.kyc.email}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, email: e.target.value } })} /></div>
                <div><Label>Mobile *</Label><Input value={testCreate.kyc.mobileno}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, mobileno: e.target.value } })} /></div>
                <div><Label>DOB *</Label><Input type="date" value={testCreate.kyc.dateofbirth}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, dateofbirth: e.target.value } })} /></div>
                <div><Label>Aadhar / ID No *</Label><Input value={testCreate.kyc.idproof_no}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, idproof_no: e.target.value } })} /></div>
                <div><Label>Pincode *</Label><Input value={testCreate.kyc.pincode}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, pincode: e.target.value } })} /></div>
                <div><Label>City *</Label><Input value={testCreate.kyc.cityname}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, cityname: e.target.value } })} /></div>
                <div><Label>District *</Label><Input value={testCreate.kyc.district}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, district: e.target.value } })} /></div>
                <div><Label>Application No (apno)</Label><Input value={testCreate.kyc.apno}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, apno: e.target.value } })} /></div>
                <div><Label>Partner ID (provider)</Label><Input value={testCreate.kyc.partnerid}
                         onChange={e => setTestCreate({ ...testCreate, kyc: { ...testCreate.kyc, partnerid: e.target.value } })} /></div>
              </div>
              <Button onClick={runTestCreate} disabled={testBusy} data-testid="test-create-run">
                {testBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Validate + Create
              </Button>
            </TabsContent>
          </Tabs>

          {testResult && (
            <div className="mt-3 border rounded p-3 bg-slate-50 max-h-72 overflow-y-auto">
              <p className="text-xs font-semibold text-slate-500 mb-1">Provider Response</p>
              <pre className="text-xs whitespace-pre-wrap text-slate-800">{JSON.stringify(testResult, null, 2)}</pre>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Combo Form Dialog */}
      <Dialog open={showComboForm} onOpenChange={setShowComboForm}>
        <DialogContent className="max-w-lg" data-testid="railtel-combo-dialog">
          <DialogHeader>
            <DialogTitle>{editingCombo ? 'Edit Combo' : 'New ANT+RAILTEL Combo'}</DialogTitle>
            <DialogDescription>Bundle a Railtel package + an IPTV package at one combined price.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Combo Name *</Label>
              <Input value={comboForm.name} onChange={e => setComboForm({ ...comboForm, name: e.target.value })} placeholder="e.g. ANT+RAILTEL Basic 30D" data-testid="combo-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Railtel Package *</Label>
                <Select value={comboForm.railtel_package_id} onValueChange={v => setComboForm({ ...comboForm, railtel_package_id: v })}>
                  <SelectTrigger data-testid="combo-form-rpkg"><SelectValue placeholder="Pick…" /></SelectTrigger>
                  <SelectContent>
                    {packages.filter(p => p.is_active !== false).map(p => (
                      <SelectItem key={p.package_id} value={p.package_id}>{p.name} ({RS(p.master_price)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>IPTV Package *</Label>
                <Select value={comboForm.iptv_package_id} onValueChange={v => setComboForm({ ...comboForm, iptv_package_id: v })}>
                  <SelectTrigger data-testid="combo-form-ipkg"><SelectValue placeholder="Pick…" /></SelectTrigger>
                  <SelectContent>
                    {iptvPackages.map(p => (
                      <SelectItem key={p.package_id} value={p.package_id}>{p.name} ({RS(p.master_price || p.price)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Master Price (Rs.) *</Label>
                <Input type="number" min={1} step="0.01" value={comboForm.master_price}
                       onChange={e => setComboForm({ ...comboForm, master_price: e.target.value })}
                       data-testid="combo-form-price" />
              </div>
              <div>
                <Label>Validity (days)</Label>
                <Input type="number" min={1} value={comboForm.validity_days}
                       onChange={e => setComboForm({ ...comboForm, validity_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={comboForm.description} onChange={e => setComboForm({ ...comboForm, description: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={comboForm.is_active} onCheckedChange={v => setComboForm({ ...comboForm, is_active: v })} />
              <Label className="text-sm">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComboForm(false)}>Cancel</Button>
            <Button onClick={saveCombo} data-testid="combo-form-save">{editingCombo ? 'Save Changes' : 'Create Combo'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Combo Partner Pricing Dialog */}
      <Dialog open={!!comboPricingPkg} onOpenChange={v => { if (!v) setComboPricingPkg(null); }}>
        <DialogContent data-testid="combo-pricing-dialog">
          <DialogHeader>
            <DialogTitle>Partner Pricing — {comboPricingPkg?.name}</DialogTitle>
            <DialogDescription>Override the combo price for a specific partner. Master price: {RS(comboPricingPkg?.master_price || 0)}</DialogDescription>
          </DialogHeader>
          {comboPricingPkg?.custom_pricing?.length > 0 && (
            <div className="space-y-1 mb-2">
              <p className="text-xs font-medium text-slate-500">Existing overrides:</p>
              {comboPricingPkg.custom_pricing.map((cp, i) => {
                const p = partners.find(x => x.customer_id === cp.customer_id);
                return <p key={i} className="text-xs"><span className="font-medium">{p?.company_name || cp.customer_id}</span>: {RS(cp.price)}</p>;
              })}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label>Partner *</Label>
              <Select value={comboPricingForm.customer_id} onValueChange={v => setComboPricingForm({ ...comboPricingForm, customer_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick a partner..." /></SelectTrigger>
                <SelectContent>
                  {partners.map(p => (
                    <SelectItem key={p.customer_id} value={p.customer_id}>{p.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Price for this Partner *</Label>
              <Input type="number" min={1} step="0.01" value={comboPricingForm.price}
                     onChange={e => setComboPricingForm({ ...comboPricingForm, price: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComboPricingPkg(null)}>Cancel</Button>
            <Button onClick={saveComboPartnerPricing}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Top-up Dialog */}
      <Dialog open={showTopup} onOpenChange={setShowTopup}>
        <DialogContent data-testid="railtel-topup-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Railtel Wallet — Partner / LCO</DialogTitle>
            <DialogDescription>
              {topupMode === 'credit'
                ? "Credits the selected partner/LCO's Railtel wallet without going through the payment gateway. Use for offline payments (cash, NEFT, cheque)."
                : "Debits (deducts) the selected partner/LCO's Railtel wallet. Reason is required for audit."}
            </DialogDescription>
          </DialogHeader>
          <Tabs value={topupMode} onValueChange={setTopupMode}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="credit" data-testid="railtel-tab-credit">Credit (Top-up)</TabsTrigger>
              <TabsTrigger value="debit" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700" data-testid="railtel-tab-debit">Debit (Deduct)</TabsTrigger>
            </TabsList>
            <TabsContent value="credit" className="space-y-3 pt-3">
            <div>
              <Label>Customer (Partner or LCO) *</Label>
              <Select value={topupForm.customer_id} onValueChange={v => setTopupForm({ ...topupForm, customer_id: v })}>
                <SelectTrigger data-testid="railtel-topup-customer"><SelectValue placeholder="Select a customer..." /></SelectTrigger>
                <SelectContent>
                  {customers.filter(c => c.customer_type === 'partner').length > 0 && (
                    <>
                      {customers.filter(c => c.customer_type === 'partner').map(c => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          {c.company_name} — Partner
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {customers.filter(c => c.parent_partner_id).map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.company_name} — LCO
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (Rs.) *</Label>
                <Input
                  type="number" min={1} step="0.01"
                  value={topupForm.amount}
                  onChange={e => setTopupForm({ ...topupForm, amount: e.target.value })}
                  data-testid="railtel-topup-amount"
                />
                <div className="flex flex-wrap gap-1 mt-1">
                  {[500, 1000, 2000, 5000].map(a => (
                    <Button key={a} type="button" variant="outline" size="sm"
                            onClick={() => setTopupForm({ ...topupForm, amount: String(a) })}>
                      {RS(a)}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={topupForm.payment_mode} onValueChange={v => setTopupForm({ ...topupForm, payment_mode: v })}>
                  <SelectTrigger data-testid="railtel-topup-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="neft">NEFT/RTGS</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Transaction Reference (optional)</Label>
              <Input
                value={topupForm.transaction_ref}
                onChange={e => setTopupForm({ ...topupForm, transaction_ref: e.target.value })}
                placeholder="UTR / cheque no / receipt #"
                data-testid="railtel-topup-ref"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={topupForm.notes}
                onChange={e => setTopupForm({ ...topupForm, notes: e.target.value })}
                placeholder="Any internal note"
                data-testid="railtel-topup-notes"
              />
            </div>
            </TabsContent>
            <TabsContent value="debit" className="space-y-3 pt-3">
              <div>
                <Label>Customer (Partner or LCO) *</Label>
                <Select value={debitForm.customer_id} onValueChange={v => setDebitForm({ ...debitForm, customer_id: v })}>
                  <SelectTrigger data-testid="railtel-debit-customer"><SelectValue placeholder="Select a customer..." /></SelectTrigger>
                  <SelectContent>
                    {customers.filter(c => c.customer_type === 'partner').map(c => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name} — Partner</SelectItem>
                    ))}
                    {customers.filter(c => c.parent_partner_id).map(c => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name} — LCO</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount to Debit (Rs.) *</Label>
                <Input
                  type="number" min={1} step="0.01"
                  value={debitForm.amount}
                  onChange={e => setDebitForm({ ...debitForm, amount: e.target.value })}
                  data-testid="railtel-debit-amount"
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={debitForm.reason}
                  onChange={e => setDebitForm({ ...debitForm, reason: e.target.value })}
                  placeholder="e.g., Refund, Manual correction, Service closure"
                  data-testid="railtel-debit-reason"
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={debitForm.notes}
                  onChange={e => setDebitForm({ ...debitForm, notes: e.target.value })}
                  placeholder="Additional context"
                  data-testid="railtel-debit-notes"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={debitForm.allow_negative}
                  onChange={e => setDebitForm({ ...debitForm, allow_negative: e.target.checked })}
                  data-testid="railtel-debit-allow-negative" />
                Allow negative wallet balance
              </label>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopup(false)} disabled={topupBusy}>Cancel</Button>
            <Button onClick={runTopup} disabled={topupBusy}
              className={topupMode === 'credit' ? '' : 'bg-red-600 hover:bg-red-700'}
              data-testid={topupMode === 'credit' ? 'railtel-topup-submit' : 'railtel-debit-submit'}>
              {topupBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-1" />}
              {topupMode === 'credit' ? 'Credit Wallet' : 'Debit Wallet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
